import React, { useEffect } from "react";
import LmsTable from "../ui/LmsTable";
import {
  useParseRouteActions,
  useFetchData,
  checkWeekend,
  checkHoliday,
  removeTimestamp,
  removeNullFromObj,
} from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { converToStartDateToEndDateString, guid } from "../../../Helpers";
import { editPayoffTransactionFormField, PayoffQuoteLoanCommentForm, PayoffQuoteSettlementStatementForm } from "../ui/LmsSharedForms";
import LmsFormWarningTitle from "../ui/LmsFormWarningTitle";
import { LoanPropertyUnitReconveyanceForm } from "./LmsServicingUnits";
import { PayoffQuoteUnitInlineTableEdit } from "../ui/LmsSharedTables";

interface IPayoffQuote {
  additionalInterestAmount: number | null;
  additionalInterestBasis: string | null;
  additionalInterestBasisId: string | null;
  additionalInterestFloor: number | null;
  additionalInterestRate: number | null;
  additionalPayoffFee: number | null;
  borrowerOrganization: string | null;
  collateralProperty: "Yes" | "No" | null;
  createdBy: string | null;
  createdOn: string | null;
  description: string | null;
  documentFileName: string | null;
  documentLogId: string | null;
  extensionFee: number | null;
  filterLoanId: string | null;
  filterLoanOfficerOrganizationContactCsvid: string | null;
  filterOrganizationId: string | null;
  frozen: "Yes" | "No" | null;
  fund: string | null;
  id: string;
  interestPdstartPdend1: number | null;
  interestPdstartPdend2: number | null;
  interestReserveBalance: number | null;
  loan: string | null;
  loanId: string | null;
  loanPhase: string | null;
  loanPhaseLoanStatusDisplayId: string | null;
  loanPropertyId: string | null;
  loanPropertyUnitReconveyance: string | null;
  loanPropertyUnitReconveyanceId: string | null;
  loanStatus: string | null;
  loanSubStatus: string | null;
  lptransactionAmount: number | null;
  lptransactionEffDate: string | null;
  markedPaidOff: boolean | null;
  miniLoans: string | null;
  owner: string | null;
  payoffQuoteAmount: number | null;
  payoffRequestDate: string | null;
  payoffRequestVersionNumber: number | null;
  payoffResidualHandlingOption: string | null;
  payoutDate: string | null;
  pcmarkedPaidOff: boolean | null;
  pcpayoffFundsSentDate: string | null;
  pcpayoutAmountSent: number | null;
  perDiemInterest: number | null;
  property: string | null;
  receivedAmount: number | null;
  reconveyanceFee: number | null;
  rowNoId: number | null;
  specialPurposeEntity: string | null;
  sweepAmount: number | null;
  sweepPercentage: number | null;
  totalNonCollateralUnPaidUnits: number | null;
  totalPayout: number | null;
  updatedBy: string | null;
  updatedOn: string | null;
  validThroughDate: string | null;
}

interface LmsServicingPayoffQuotesMainPageProps {
  payoffQuotes: IPayoffQuote[];
  isPayoffQuotesLoading?: boolean;
}

interface LmsServicingPayoffQuotesAddPayoffFormProps {
  id: string;
}

interface LmsServicingPayoffQuotesPartialPayoffProps
  extends LmsServicingPayoffQuotesMainPageProps {
  loanId: string;
}

interface LmsServicingPayoffQuotesAddPartialPayoffFormProps {
  unitId: string;
  loanId: string;
}

const PAYOFF_QUOTES_BASE_PATH = "/servicing-payoff-quotes";
const PAYOFF_QUOTES_ROUTES = {
  VIEW_LOAN: "view-loan",
  ELEGIBLE: "elegible-payoff",
  ADD_PAYOFF: "add-payoff",
  PARTIAL_PAYOFF: "partial-payoff",
  ADD_PARTIAL_PAYOFF: "add-partial-payoff",
};

const handleDisplayRofr = (formValue) => {
  if (formValue.includeRofr || formValue.rofrpercent || formValue.roframount || formValue.customizeRofr || formValue.rofrpending || formValue.rofrcharged || formValue.rofrcustomAmount) return true;
  return false;
};

const handleExceedsMaturityDateMessage = (inputs, initialDate, generateMessage = false) => {
  const maturityDate = new Date(initialDate.maturityDateCurrent);
  const payoffRequestDate = inputs?.payoffRequestDate ? new Date(inputs.payoffRequestDate) : null;
  const validThroughDate = inputs?.validThroughDate  ? new Date(inputs.validThroughDate) : "";

  if (validThroughDate && payoffRequestDate && maturityDate < validThroughDate && maturityDate < payoffRequestDate) {
    inputs.exceedsMaturityDateMessage = "Payoff Request Date and Valid Through Date exceeds Current Maturity Date.";
  }
  else if (payoffRequestDate && maturityDate < payoffRequestDate) {
    inputs.exceedsMaturityDateMessage = "Payoff Request Date exceeds Current Maturity Date.";
  }
  else if (validThroughDate && maturityDate < validThroughDate) {
    inputs.exceedsMaturityDateMessage = "Valid Through Date exceeds Current Maturity Date.";
  }

  inputs.validThroughDate = inputs?.validThroughDate === "0001-01-01T00:00:00" ? "" : inputs?.validThroughDate;
  if (generateMessage) {
    return inputs.exceedsMaturityDateMessage;
  }
};

const getTotalPayoff = (inputs, initialData, isFirstLoad = false) => {
  let totalPayoff = inputs.loanAmountCurrent + inputs.interestReserveBalance 
    + parseFloat(inputs.reconveyanceFee || 0) + parseFloat(inputs.additionalInterestAmount || 0) + parseFloat(inputs.additionalInterestFloor) 
    + parseFloat(inputs.payoffProcessingFee || 0) + parseFloat(inputs.additionalPayoffFee || 0) + parseFloat(inputs.lateFeeAmount || 0);

  if (inputs.includeSweep) {
    totalPayoff += inputs.sweepAmountApplicable ? parseFloat(inputs.sweepAmountApplicable) : 0;
  }

  if (inputs.includeRofr && inputs.customizeRofr) {
    totalPayoff += inputs.rofrcustomAmount ? parseFloat(inputs.rofrcustomAmount) : 0;
  } else if (inputs.includeRofr) {
    totalPayoff += inputs.roframount ? parseFloat(inputs.roframount) : 0;
  }

  if (initialData) {
    totalPayoff += initialData.extensionAdjustmentAmount || 0;
    if (initialData.payoffQuoteCrossCollateralLoanTemps?.length) {
      let totalPastDueAmount = 0;
      initialData.payoffQuoteCrossCollateralLoanTemps.map(crossCollateral => {
        if (crossCollateral.isSelected) {
          totalPastDueAmount += crossCollateral.pastDueAmount ? crossCollateral.pastDueAmount : 0;
        }
      });

      totalPayoff += totalPastDueAmount;
    }
  }

  if (inputs.loanDetailLoanProgramName === "Revolver") {
    totalPayoff = totalPayoff + parseFloat(inputs?.interestPdstartPdend1 || 0) + parseFloat(inputs?.interestPdstartPdend2 || 0) 
      - parseFloat(inputs.reconveyanceFee || 0) - parseFloat(inputs.lateFeeAmount || 0);
    if (isFirstLoad) {
      totalPayoff = inputs?.loanAmountCurrent;
    }
  }

  inputs.totalPayoff = totalPayoff;
};

const getPartialTotalPayoff = (inputs, initialData) => {
  if (initialData?.NetProceedsOrSweepOrFlat === "Sales") {
    inputs.sweepAmountApplicable = ((inputs?.sweepAmount || 0) * (inputs?.sweepPercentage || 0)) / 100;
    if (inputs?.sweepAmountApplicable >= inputs?.partialDeedReleaseAmount) {
      inputs.includeSweep = true;
    }
    else {
      inputs.includeSweep = false;
    }
  }

  let totalPayoff = parseFloat(inputs.reconveyanceFee || 0) + parseFloat(inputs.additionalPayoffFee || 0) 
    + parseFloat(inputs.payoffProcessingFee || 0) - parseFloat(initialData.reducePayoffAmount || 0) - parseFloat(inputs.reducePayoffAmount || 0);
  const partialDeedReleaseAmount = inputs?.partialDeedReleaseAmount ? parseFloat(inputs.partialDeedReleaseAmount) : 0;
  const sweepAmountApplicable = inputs?.sweepAmountApplicable ? parseFloat(inputs.sweepAmountApplicable) : 0;
  if (inputs.includeSweep && partialDeedReleaseAmount < sweepAmountApplicable  ) {
    totalPayoff += sweepAmountApplicable;
  }
  else {
    totalPayoff += partialDeedReleaseAmount;
  }

  if (inputs.includeAdditionalInterest) {
    totalPayoff += inputs.additionalInterestFloor ? parseFloat(inputs.additionalInterestFloor) : 0;
  }

  if (inputs.customizeRofr) {
    totalPayoff += inputs.rofrcustomAmount ? parseFloat(inputs.rofrcustomAmount) : 0;
  } else if (inputs.includeRofr) {
    totalPayoff += parseFloat(inputs.rofrcustomAmount || 0) + initialData.rofrpending + initialData.rofrcharged;
  }

  if (inputs.reduceByRemainingBudget) {
    totalPayoff += initialData.remainingBudget;
  }

  inputs.totalPayoff = totalPayoff;
};

const onAddPayoffFormChange = async (options, lastUpdated, initialData, fetchData, loanId, isPartial = false) => {
  let inputs = options.inputs;
  if (lastUpdated === "includeRofr" || lastUpdated === "customizeRofr") {
    if (inputs[lastUpdated] === false) {
      inputs.rofrcustomAmount = "";
    }
  }

  if (lastUpdated === "includeSweep") {
    if (inputs.includeSweep) {
      inputs.sweepAmountApplicable = (inputs.sweepAmount * inputs.sweepPercentage) / 100;
    }
    else {
      inputs.sweepAmount = 0;
      inputs.sweepAmountApplicable = "";
      inputs.customizeRofr = false;
      inputs.rofrcustomAmount = "";
    }
  }

  if (lastUpdated === "chargePayoffFeeCheckbox") {
    inputs.payoffProcessingFee = inputs[lastUpdated] === true ? 50 : "";
  }

  if (lastUpdated === "validThroughDate") {
    const date = inputs.validThroughDate;
    handleExceedsMaturityDateMessage(inputs, initialData);
    if (checkWeekend(removeTimestamp(date)) || checkHoliday(removeTimestamp(date))) {
      options.setFormErrorMessage({
        title: "Invalid Date",
        message: "Selected date cannot be a weekend or holiday.",
      });
    } else {
      const { data: checkValidThroughDateData, error: validTroughDateError } = await fetchData(`payoffquotes/check-valid-through-date?loanId=${loanId}&validthroughdate=${date}`, { 
        fetchMethod: "GET", 
        data: { id: loanId, validThroughDate: date },
      });
      
      if (validTroughDateError) {
        options.setErrors(validTroughDateError);
      }
      
      if (checkValidThroughDateData) {
        inputs = { 
          ...inputs, 
          ...checkValidThroughDateData, 
          prorateExtension:  checkValidThroughDateData?.prorateExtension
        };

        if (inputs?.payoffRequestDate) {
          const maturityDateCurrent = new Date(inputs?.maturityDateCurrent);
          const validDate = new Date(date);
          if (inputs.prorateExtension !== true && inputs.prorateExtension !== false) {
            inputs.prorateExtension = validDate >= maturityDateCurrent ? true : false;
            inputs.applyExtensionFee = validDate >= maturityDateCurrent ? true : false;
          }
        }
      }

      const { data: interestAndExtensionFeeData, error: interestAndExtensionFeeError } = await fetchData("payoffquotes/interest-and-extension-fee", {
        fetchMethod: "POST",
        data: { 
          loanId: loanId, 
          validThroughDate: date,
          additionalInterestRate: inputs?.additionalInterestRate,
          extensionFeePercentageId: inputs?.extensionFeePercentageId,
          includePCDefaultInterestSplit: false,
          includePCExtensionAfter2: false,
          payoffRequestDate: inputs?.payoffRequestDate,
          prorateExtension: inputs?.prorateExtension,
          ...(inputs?.loanPropertyUnitReconveyanceId ? { loanPropertyUnitReconveyanceId: inputs?.loanPropertyUnitReconveyanceId } : {}),
        }
      });
      
      if (interestAndExtensionFeeError) {
        options.setErrors(interestAndExtensionFeeError);
      }
      
      if (interestAndExtensionFeeData) {
        inputs = {
          ...inputs,
          ...interestAndExtensionFeeData,
          extensionFeeAfter2: interestAndExtensionFeeData?.pcExtensionFeeAfter2,
          interestPdstartPdend1: interestAndExtensionFeeData?.interestAmount1,
          interestPdstartPdend2: interestAndExtensionFeeData?.interestAmount2,
          pdPeriod1: converToStartDateToEndDateString(interestAndExtensionFeeData.startDate1, interestAndExtensionFeeData.endDate1),
          pdPeriod2: converToStartDateToEndDateString(interestAndExtensionFeeData.startDate2, interestAndExtensionFeeData.endDate2),
          pdStartDate1: interestAndExtensionFeeData.startDate1,
          pdEndDate1: interestAndExtensionFeeData.endDate1,
          pdStartDate2: interestAndExtensionFeeData.startDate2,
          pdEndDate2: interestAndExtensionFeeData.endDate2,
          isPdPeriod1Display: (interestAndExtensionFeeData.startDate1 !== null && interestAndExtensionFeeData.endDate1 !== null),
          isPdPeriod2Display: (interestAndExtensionFeeData.startDate2 !== null && interestAndExtensionFeeData.endDate2 !== null),
          lateFeeAmount: interestAndExtensionFeeData.lateFeeAmount1 + interestAndExtensionFeeData.lateFeeAmount2,
          additionalInterestAmount: interestAndExtensionFeeData?.extraAdditionalInterest,
          commentsHtml: interestAndExtensionFeeData?.interestAndExtensionFeeData
        };

        options.setMetaData((prevMetaData) => { return { 
          ...prevMetaData, 
          commentsHtml: interestAndExtensionFeeData?.commentsHtml?.length ? interestAndExtensionFeeData?.commentsHtml?.split("<br />") : null 
        }; });
      }
    }
  }

  isPartial ? getPartialTotalPayoff(inputs, initialData) : getTotalPayoff(inputs, initialData);
  options.setInputs({ ...inputs, isFormEdit: true });
};

const LmsServicingPayoffQuotesMainPage = () => {
  return (
    <LmsTable
      apiSuffix="payoffquotes"
      rowsBackgroundCustomColor={(rowData) => rowData.frozen === "Yes" ? "cyan" : ""}
      actionButtons={[
        {
          action: "add",
          path: `${PAYOFF_QUOTES_BASE_PATH}/${PAYOFF_QUOTES_ROUTES.ELEGIBLE}`,
        },
        "export",
      ]}
      columnLabelsDictionary={{
        borrowerOrganization: "Borrower",
      }}
      columnClickOptions={{
        documentFileName: { downloadPath: "document/download", idProp: "documentLogId" },
        loan: { path: "servicing-payoff-quotes/view-loan", idProp: "loanId" }
      }}
    />
  );
};

const LmsServicingPayoffQuotesLoanForm = ({ loanId, payoffQuotes, isPayoffQuotesLoading }) => {
  const currentPayoffQuote = payoffQuotes.find((payoffQuote) => payoffQuote.loanId === loanId) || {};
  const [{ data: loanDetail, isLoading: isLoanDetailLoading }] = useFetchData(`payoffquotes/${currentPayoffQuote.id}`);
  const handleFormatLoanInfo = (currentPayoffQuote) => {
    let additionalInfo = {};
    if (currentPayoffQuote?.sweepAmount === 0 && currentPayoffQuote?.sweepPercentage === 0) {
      currentPayoffQuote.includeSweep = false;
      additionalInfo = {...currentPayoffQuote, includeSweep: false };
    }
    else {
      additionalInfo = {...currentPayoffQuote, includeSweep: true };
    }

    return { ...additionalInfo, 
      ...loanDetail,
      loanId: loanDetail?.loanId,
      payoffEscrowNo: loanDetail?.payoffEscrowNo,
      bankInformationId: loanDetail?.bankInformationId,
      refi: loanDetail?.refi,
      interestRateCurrent: loanDetail?.interestRateCurrent,
      loanAmountCurrent: loanDetail?.loanAmountCurrent,
      prorateExtension: loanDetail?.prorateExtension,
      daysInExtension: loanDetail?.daysInExtension,
      lateFeeAmount: loanDetail?.lateFeeAmount,
      fundOrganizationId: loanDetail?.fundOrganizationId,
      payoffQuoteAmount: loanDetail?.payoffQuoteAmount,
      loanDetailSweep: loanDetail?.loanDetailSweep,
      pdStartDate1: loanDetail?.pdStartDate1,
      pdEndDate1: loanDetail?.pdEndDate1,
      pdStartDate2: loanDetail?.pdStartDate2,
      pdEndDate2: loanDetail?.pdEndDate2,
      loanSubStatusId: loanDetail?.loanSubStatusId,
      interestPdstartPdend1: loanDetail?.interestPdstartPdend1,
      interestPdstartPdend2: loanDetail?.interestPdstartPdend2,
    };
  };

  const myForm = editPayoffTransactionFormField(new BCFormController(handleFormatLoanInfo(currentPayoffQuote), { initIsDisabled: true }));
  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={["close"]}
      isLoading={isPayoffQuotesLoading || isLoanDetailLoading}
    />
  );
};

const formatSalePriceDataFunc = (data, options) => {
  if (data?.actualSalePrices) {
    options.setInputs((prevInputs) => { return { ...prevInputs, partialDeedReleaseAmount: data?.partialDeedReleaseAmount }; });
    if (!options?.inputs?.loanDetailLoanSubTypeName?.includes("Refi")) {
      return data?.actualSalePrices.map(item => {return { unit: item.unit, appraisedValue: item.actualSalePrice };});
    }

    return data?.actualSalePrices;
  }

  return [];
};

const addPayoffFormFields = (
  form: BCFormController
): BCFormController => {
  const interestPdstartPdend1labelFunc = (options) => { return options?.inputs?.pdPeriod1 || null; };
  const interestPdstartPdend2labelFunc = (options) => { return options?.inputs?.pdPeriod2 || null; };
  form.addGroupFields([
    form.getCurrencyField("Current Commitment Balance", { 
      name: "loanAmountCurrent", 
      required: true, 
      disabled: true,
      isHidden: { loanDetailLoanProgramName: "Revolver" }, 
      isHiddenOperation: "notequal"
    }),
    form.getCurrencyField("Overall Interest Reserve Balance", { 
      name: "interestReserveBalance", 
      required: true, 
      disabled: true,
      isHidden: { loanDetailLoanProgramName: "Revolver" }, 
      isHiddenOperation: "notequal" 
    }),
    form.getCurrencyField("interestPdstartPdend1", { 
      labelFunc : interestPdstartPdend1labelFunc,
      name: "interestPdstartPdend1", 
      required: true, 
      disabled: true,
      isHidden: { isPdPeriod1Display: true }, isHiddenOperation: "notequal" 
    }),
    form.getCurrencyField("interestPdstartPdend2", { 
      labelFunc : interestPdstartPdend2labelFunc,
      name: "interestPdstartPdend2", 
      required: true, 
      disabled: true,
      isHidden: { isPdPeriod2Display: true }, isHiddenOperation: "notequal" 
    }),
    form.getBlankField({ isHiddenFunc: (options) => options?.inputs?.isPdPeriod2Display !== false })
  ]);

  const extensionFeelabelFunc = (options) => options?.inputs?.extensionModificationStatus 
  ? `Extension Fee ${options?.inputs?.extensionModificationStatus}` 
  : "Extension Fee";

  if (form?.values?.loanDetailLoanProgramName !== "Revolver") {
    form.addCenteredGroup([form.getButtonField("Edit", undefined, {
      modalContent: (options) => <PayoffQuoteUnitInlineTableEdit options={options} />
    })]);

    form.addComponent((options) => 
      <LmsTable 
        key="actual-sale-price-table"
        apiSuffix={options?.inputs?.loanId ? `payoffquotes/actual-sale-price-data/${options?.inputs?.loanId}` : ""}
        isDisabled
        formatDataFunc={(data) => formatSalePriceDataFunc(data, options)}
        refreshDataDependency={options?.inputs?.lastUpdatedUnitId}
        columnLabelsDictionary={{actualSalesPrice: "Appraised Value"}}
      />
    );

    form.addGroupFields([
      form.getCurrencyField("Current Loan Balance", { name: "loanAmountCurrent", required: true, disabled: true }),
      form.getCurrencyField("Interest Reserve Balance", { required: true, disabled: true }),
      form.getInputField("Current Interest Rate", { name: "interestRateCurrent", required: true, disabled: true })
    ]);

    form.addGroupFields([
      form.getCheckboxField("Apply Extension Fee"),
      form.getDropdownField("Fee %", "metadata/list-extension-fee-percentages", { name: "extensionFeePercentageId" }),
    ], { isHidden: { showApplyExtension: true }, isHiddenOperation: "notequal" });

    form.addGroupFields([
      form.getCurrencyField("Per Diem Interest", { required: true, disabled: true }),
      form.getCheckboxField("Prorate Extension", { inline: true }),
      form.getCurrencyField("Extension Fee", { 
        labelFunc: extensionFeelabelFunc,
        required: true, 
        disabled: true 
      }),
      form.getInputField("Per Diem Extension", { disabled: true }),
    ]);
  } else {
    form.addGroupFields([
      form.getCheckboxField("Prorate Extension", { inline: true }),
      form.getCurrencyField("Extension Fee", { 
        labelFunc: extensionFeelabelFunc,
        required: true, 
        disabled: true 
      }),
      form.getInputField("Per Diem Extension", { disabled: true }),
      form.getBlankField()
    ]);
  }


  form.addGroupFields([
    form.getCheckboxField("Include Sweep", { 
      inline: true,
      isDisabledOn: { netProceedsOrSweepOrFlat: "Flat" }, 
      isDisabledOnOperation: "equal"
    }),
    form.getCurrencyField("Sweep Amount", { 
      fieldType: "dollar", 
      required: true,
    }),
    form.getDisplayField("Sweep %", { 
      name: "sweepPercentage", 
      fieldType: "float"
    }),
    form.getCurrencyField("Sweep Amount Applicable", { 
      name: "sweepAmountApplicable", 
      isDisabledOn: { includeSweep: true }, 
      isDisabledOnOperation: "notequal", 
      disabled: true 
    })
  ]);
  
  form.addGroupFields([
    form.getDisplayField("Additional Interest Basis", { name: "additionalInterestBasisDisplay" }),
    form.getCurrencyField("Additional Interest Floor", { disabled: true }),
    form.getInputField("Additional Interest %", { name: "additionalInterestRate", required: true }),
    form.getCurrencyField("Extra Additional Interest", { name: "additionalInterestAmount", required: true }),
  ]);
  
  form.addGroupFields([
    form.getCurrencyField("Reconveyance Fee", { 
      required: true,
      isHidden: { loanDetailLoanProgramName: "Revolver" }, 
      isHiddenOperation: "equal"
    }),
    form.getDropdownField("Additional Payoff Fees Option", "metadata/list-transaction-types", { name: "transactionTypeId" }),
    form.getCurrencyField("Additional Payoff Fees Amount", { name: "additionalPayoffFee" }),
    form.getCurrencyField("Late Fee Amount", { 
      disabled: true,
      isHidden: { loanDetailLoanProgramName: "Revolver" }, 
      isHiddenOperation: "equal"
    })
  ]);
  
  form.addGroupFields([
    form.getCheckboxField("Charge Payoff Fee", { name: "chargePayoffFeeCheckbox", inline: true }),
    form.getCurrencyField("Payoff Processing Fee", { 
      isDisabledOn: { chargePayoffFeeCheckbox: true }, 
      isDisabledOnOperation: "notequal",
      name: "payoffProcessingFee"
    }),
    form.getCurrencyField("Previous Fee", { disabled: true }),
    (handleDisplayRofr(form.initialValues) 
      ? form.getBlankField({ isHidden: { loanDetailLoanProgramName: "Revolver" }, isHiddenOperation: "equal" }) 
      : form.getCurrencyField("Total Payoff", { disabled: true })
    )
  ]);

  if (form?.initialValues && handleDisplayRofr(form.initialValues)) {
    form.addGroupFields([
      form.getCheckboxField("Include ROFR", { inline: true, name: "includeRofr" }),
      form.getInputField("ROFR Percent", { 
        disabled: true, 
        fieldType: "float",
        isHidden: { includeRofr: true }, 
        isHiddenOperation: "notequal",
        name: "rofrpercent" 
      }),
      form.getCurrencyField("ROFR Amount", {
        isDisabledOn: { includeRofr: true }, 
        isDisabledOnOperation: "notequal",
        isHidden: { includeRofr: true }, 
        isHiddenOperation: "notequal",
        name: "roframount"
      })
    ]);

    form.addGroupFields([
      form.getCheckboxField("Customize ROFR", { 
        inline: true,
        name: "customizeRofr",
        isHidden: { includeRofr: true }, 
        isHiddenOperation: "notequal"
      }),
      form.getCurrencyField("ROFR Custom Amount", { 
        name: "rofrcustomAmount",
        isHidden: { includeRofr: true }, 
        isHiddenOperation: "notequal",
        isDisabledOn: { customizeRofr: true }, 
        isDisabledOnOperation: "notequal", 
      }),
      form.getCurrencyField("Total Payoff", { disabled: true })
    ]);
  }

  return form;
};

const LmsServicingPayoffQuotesAddPayoffForm: React.FC<LmsServicingPayoffQuotesAddPayoffFormProps> = ({ id }) => {
  const [{ data: initialPayoffFormData, isLoading: isInitialDataLoading, error, fetchData }] = useFetchData(`payoffquotes/initial-data/${id}`);
  // TODO: Revolver Loan Payoff Quote + Next button (to a page that shows revolver summary), after Sprint work 14024 (reference sprint work 13788, test with 72901M)
  const myForm = new BCFormController({ ...initialPayoffFormData, exceedsMaturityDateMessage: "" }, { 
    values: { 
      loanId: id, 
      netProceedsOrSweepOrFlat: initialPayoffFormData?.netProceedsOrSweepOrFlat,
      showServicingStatusMessage: (initialPayoffFormData?.loanPhaseName === "Servicing" && initialPayoffFormData?.loanStatusName !== "Active" ),
      loanDetailRightOfFirstRefusal: initialPayoffFormData?.loanDetailRightOfFirstRefusal,
      maturityDateCurrent: initialPayoffFormData?.maturityDateCurrent,
      loanDetailLoanSubTypeName: initialPayoffFormData?.loanDetailLoanSubTypeName,
      loanDetailLoanProgramName: initialPayoffFormData?.loanDetailLoanProgramName
    },
    metaData: {},
    submitSuffix: `payoffquotes/generate-payoff-quotation/${id}`,
    submitMethod: "POST",
    dataFormatterFunc: (data) => { return {...removeNullFromObj({ ...data }), id: guid(), loanId: id, additionalInterestBasisId: initialPayoffFormData?.additionalInterestBasisId }; },
    onSaveContinue: (response, options) => { console.log("hi"); console.log("response", response); console.log("options", options); }
  });

  addBasicPayoffFormFields(myForm);
  addPayoffFormFields(myForm);
  addPayoffQuotesComments(myForm);

  useEffect(() => {
    if (initialPayoffFormData) {
      getTotalPayoff(initialPayoffFormData, initialPayoffFormData, true);
      handleExceedsMaturityDateMessage(initialPayoffFormData, initialPayoffFormData);
    }
  }, [initialPayoffFormData]);

  return <LmsFormGenerator
    myForm={myForm}
    actions={[
      ...(initialPayoffFormData?.loanDetailLoanProgramName === "Revolver" 
      ? [{ action: "save-continue", text: "Next", actionSubmitSuffix: `payoffquotes/create/${id}`, actionSubmitMethod: "POST" }]
      : [{ action: "save-close", text: "Generate Payoff Quotation" }] 
      ),
      "close"
    ]}
    isLoading={isInitialDataLoading}
    error={error} 
    onFormChanged={(options, lastUpdated) => onAddPayoffFormChange(options, lastUpdated, initialPayoffFormData, fetchData, id)}
  />;
};

const LmsServicingPayoffQuotesPartialPayoff: React.FC<
  LmsServicingPayoffQuotesPartialPayoffProps
> = ({ loanId }) => {
  return (
    <LmsTable
      skipClick
      actionButtons={[
        "export",
        {
          action: "edit",
          text: "Select",
          path: `${PAYOFF_QUOTES_BASE_PATH}/${PAYOFF_QUOTES_ROUTES.ADD_PARTIAL_PAYOFF}/{id}?loanId=${loanId}`,
        isDisabled: (checkedItems) => checkedItems?.length !== 1,
        },
      ]}
      columnLabelsDictionary={{
        description: "Comments / Instructions",
        brms: "BRMs",
        initialCos: "Initial COs",
        reviewCos: "Review COs",
        borrowerOrganization: "Borrower",
        applicationNumber: "Application #",
        loanNumber: "Loan #",
        partialDeedReleased: "Paid Off"
      }}
      apiSuffix={`/payoffquotes/property-unit-reconveyance/${loanId}`}
      rowsBackgroundCustomColor={(row) => row.colorId}
    />
  );
};

const addBasicPayoffFormFields = (form: BCFormController) => {
  form.addCenteredGroup([form.getComponent(<LmsFormWarningTitle message="This loan has ROFR Language" />, { width: 16 })], {
    isHiddenFunc: ({ inputs }) => { return inputs?.loanDetailRightOfFirstRefusal !== true; }
  }); 

  form.addCenteredGroup([form.getComponent(<LmsFormWarningTitle message="Loan is in Servicing-Default. Please talk to legal counsel to generate payoff quote" />, { width: 16 })], {
    isHiddenFunc: ({ inputs }) => { return inputs?.showServicingStatusMessage !== true; }
  }); 

  form.addCenteredGroup([form.getComponent(<LmsFormWarningTitle message="Actual Sales Price has not been filled in for the unit" />, { width: 16 })], {
    isHiddenFunc: ({ inputs }) => { return inputs?.showActualSalesPriceMessage !== true; }
  }); 

  form.addGroupFields([
    form.getDropdownField("Borrower", "metadata/list-borrower-organizations", { name: "borrowerOrganizationId", disabled: true }),
    form.getDisplayField("Special Purpose Entity", { name: "specialPurposeEntityDisplay" }),
    form.getDisplayField("Loan", { name: "loanDisplay" }),
    form.getDropdownField("Fund", "metadata/list-organization-funds", { name: "fundOrganizationId" })
  ]);

  form.addGroupFields([
    form.getDropdownField("Bank", "payoffquotes/list-fund-bank-info", { 
      dependencyField: "fundOrganizationId",
      name: "bankInformationId", 
      required: true 
    }),
    form.getInputField("Escrow #", { name: "payoffEscrowNo" }),
    form.getCurrencyField("Original Loan Amount", { name: "loanDetailLoanAmountOriginal", fieldType: "dollar", disabled: true }),
    form.getDisplayField("Refi?", { name: "refi" })
  ]);

  if (form?.initialValues?.payoffRequestDate && form?.initialValues?.maturityDateCurrent) {
    const message = handleExceedsMaturityDateMessage({...form?.initialValues}, {...form?.initialValues}, true);
    form.addCenteredGroup([
      form.getComponent(<LmsFormWarningTitle message={message} />, { width: 16 }),
    ], { isHiddenFunc: (options) => options?.inputs?.exceedsMaturityDateMessage, widths: 16 } );
  }

  const today = new Date();
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth()+1, 0);
  const lastDayOfNextMonth = new Date(today.getFullYear(), today.getMonth()+2, 0);
  form.addCenteredGroup([
    form.getComponent(({ inputs }) => {
      return <LmsFormWarningTitle message={inputs?.exceedsMaturityDateMessage} />;
    }, { width: 16 }),
  ], { isHiddenFunc: (options) => !options?.inputs?.exceedsMaturityDateMessage, widths: 16 } );

  form.addGroupFields([
    form.getCalendarField("Payoff Request Date", { 
      required: true,
      minDate: 0,
      maxDate: lastDayOfMonth.toISOString().substring(0,10)
    }),
    form.getCalendarField("Valid Through Date", { 
      required: true,
      minDate: 0,
      maxDate: lastDayOfNextMonth.toISOString().substring(0,10)
    }),
    form.getDisplayField("Days In Extension"),
    form.getBlankField()
  ]);

  form.addGroupFields([
    form.getLabelField("Mini Loans", { width: 4 }),
    form.getGroupRadioField("All Mini Loans", { value: "All" }),
    form.getGroupRadioField("Selected Mini Loans", { value: "selected" }),
    form.getDropdownField("", "loans/list-servicing-mini-loans", {
      dropdownMultiple: true,
      required: true,
      name: "miniLoanIds",
      dependencyField: "loanId",
      isHidden: { miniLoanOption: "selected" }, 
      isHiddenOperation: "notequal",
      width: 14
    }),
    form.getBlankField({ 
      isHidden: { miniLoanOption: "selected" }, 
      isHiddenOperation: "equal",
      width: 14
    })
  ], { 
    radioGroupName: "miniLoanOption",
    radioGroupValue: "All", 
    isHidden: { loanDetailLoanProgramName: "Revolver" }, 
    isHiddenOperation: "notequal" 
  });
};

const addPartialPayoffFields = (
  form: BCFormController
): BCFormController => {
  form.addCenteredGroup([
    form.getLabelField(form?.values?.address, { width: "12" }),
    form.getButtonField("Edit", undefined, {
      modalContent: (setIsModalOpen, options) => (
        <LoanPropertyUnitReconveyanceForm 
          unitId={form?.values?.loanPropertyUnitReconveyanceId} 
          type="edit" 
          payoff 
          handleClose={() => setIsModalOpen(false)}
          payoffQuoteOptions={options}
        />
      ),
      width: "4",
    }),
  ]);

  form.addComponent((options) => 
    <LmsTable 
      key="actual-sale-price-table"
      apiSuffix={(options?.inputs?.loanId && form?.values?.loanPropertyUnitReconveyanceId) 
        ? `payoffquotes/actual-sale-price-data/${options?.inputs?.loanId}/${form?.values?.loanPropertyUnitReconveyanceId}` 
        : ""}
      isDisabled
      formatDataFunc={(data) => formatSalePriceDataFunc(data, options)}
      refreshDataDependency={options?.inputs?.lastUpdatedUnitId}
      compact
      {...(form?.values?.loanDetailLoanSubTypeName?.includes("Refi") === false ? { columnLabelsDictionary: { actualSalesPrice: "Appraised Value" }} : {})}
    />
  );

  const sweeplabel = form?.values?.netProceedsOrSweepOrFlat === "Net Proceeds" 
    ? "Net Proceeds" : form?.values?.netProceedsOrSweepOrFlat === "Sales"
    ? "Actual Sales Price" : "Sweep";
  form.addGroupFields([
    form.getCheckboxField("Include Sweep", { inline: true }),
    form.getCurrencyField(sweeplabel, { 
      isDisabledOnFunc: ({ inputs }) => !inputs.IncludeSweep || inputs.NetProceedsOrSweepOrFlat !== "Net Proceeds",
      fieldType: "dollar", 
      required: true,
      name: "sweepAmount"
    }),
    form.getDisplayField("Sweep %", { 
      name: "sweepPercentage", 
      fieldType: "float" 
    }),
    form.getDisplayField("Sweep Amount Applicable", { 
      name: "sweepAmountApplicable", 
      isDisabledOn: { NetProceedsOrSweepOrFlat: "Sales" }, 
      isDisabledOnOperation: "notequal", 
      fieldType: "dollar" 
    })
  ]);

  form.addGroupFields([
    form.getCheckboxField("Include Additional Interest", { disabled: true, inline: true }),
    form.getDisplayField("Additional Interest Basis", { name: "additionalInterestBasisDisplay" }),
    form.getCurrencyField("Additional Interest Floor", { required: true, disabled: true }),
    form.getCurrencyField("Reconveyance Fee", { required: true })
  ]);

  form.addGroupFields([
    form.getDropdownField("Additional Payoff Fees Option", "metadata/list-transaction-types", { name: "transactionTypeId" }),
    form.getCurrencyField("Additional Payoff Fees Amount", { name: "additionalPayoffFee" }),
    form.getCheckboxField("Charge Payoff Fee", { name: "chargePayoffFeeCheckbox", inline: true }),
    form.getInputField("Payoff Processing Fee", { 
      isDisabledOn: { chargePayoffFeeCheckbox: true }, 
      isDisabledOnOperation: "notequal", 
      name: "payoffProcessingFee"
    }),
  ]);

  form.addGroupFields([
    form.getCurrencyField("Previous Fee", { disabled: true }),
    form.getCurrencyField("Reduce Payoff Amount"),
    form.getCheckboxField("Reduce By Remaining Budget", { inline: true }),
    form.getCurrencyField("Remaining Budget", { disabled: true })
  ]);

  if (form?.initialValues && handleDisplayRofr(form.initialValues)) {
    form.addGroupFields([
      form.getCheckboxField("Apply Extension Credit", { inline: true }),
      form.getCurrencyField("Extension Credit", { disabled: true }),
      form.getCheckboxField("Include ROFR", { inline: true, name: "includeRofr" }),
      form.getInputField("ROFR Percent", { disabled: true, fieldType: "float", name: "rofrpercent" })
    ]);

    form.addGroupFields([
      form.getCurrencyField("ROFR Amount", {
        isDisabledOn: { includeRofr: true }, 
        isDisabledOnOperation: "notequal", 
        name: "roframount"
      }),
      form.getCheckboxField("Customize ROFR", { 
        inline: true,
        name: "customizeRofr",
        isHidden: { includeRofr: true }, 
        isHiddenOperation: "notequal"
      }),
      form.getCurrencyField("ROFR Custom Amount", { 
        name: "rofrcustomAmount",
        isHidden: { includeRofr: true }, 
        isHiddenOperation: "notequal",
        isDisabledOn: { customizeRofr: true }, 
        isDisabledOnOperation: "notequal", 
      }),
      form.getCurrencyField("Total Payoff", { disabled: true })
    ]);
  } else {
    form.addGroupFields([
      form.getCheckboxField("Apply Extension Credit"),
      form.getCurrencyField("Extension Credit"),
      form.getCurrencyField("Total Payoff", { disabled: true }),
      form.getBlankField()
    ]);
  }

  form.addGroupFields([
    form.getTextareaField("Sweep Provision", { disabled: true }),
    form.getTextareaField("Partial Deed Release Provision", { disabled: true }),
  ]);

  form.addGroupFields([
    form.getTextareaField("Partial Deed Release Option", { name: "partialDeedReleaseOption", disabled: true, width: 15}),
    form.getCurrencyField("Partial Deed Release Amount", { disabled: true }),
  ]);

  return form;
};

const addPayoffQuotesComments = (form: BCFormController) => {
  form.addGroupFields([
    form.getComponent((options) =>
      <LmsTable
        apiSuffix={form?.values?.loanId ? `payoffquotes/payoff-temp-comments/${form?.values?.loanId}`: ""}
        displayOnlyProps={["commentSubjectDisplay", "description", "createdOn", "createdBy"]}
        columnLabelsDictionary={{commentSubjectDisplay: "Subject"}}
        title="Payoff comments"
        refreshDataDependency={options?.inputs?.lastAddedCommentId}
        compact
        isDisabled
      />,
    { width: 8 }),
    form.getComponent((options) => {
      return <>
        {options?.metaData?.commentsHtml?.length >= 1 && <div className="lms-comments-html-div">
          {options?.metaData?.commentsHtml?.map((comment) => {
            return <p className="lms-comments-html">{comment}</p>;
          })}
        </div>}
      </>;
    }, { width: 4 }),
    form.getButtonField("Add Comment", undefined, {
      modalContent: (setIsAddCommentModalOpen, options) => (
        <PayoffQuoteLoanCommentForm
          payoffQuoteOptions={options}
          id={form?.values?.loanId} 
          setIsAddCommentModalOpen={setIsAddCommentModalOpen} 
        />
      ),
      modalSize: "large",
      width: 2 
    })
  ], { blankWidth: 1 });

  form.addCenteredGroup([form.getTextareaField("Comments / Instructions", { name: "description" })]);
  form.addCenteredGroup([
    form.getButtonField("Settlement Statement / Escrow Request", undefined, {
      modalContent: (setIsModalOpen, options) => (
        <PayoffQuoteSettlementStatementForm
          id={form?.values?.loanId} 
          setIsPayoffQuoteDocumentModalOpen={setIsModalOpen} 
          payoffQuoteOptions={options}
        />
      )
    }),
    form.getComponent((options) => {
      return <div className="lms-flex-start-container">
        {options?.metaData?.uploadedSettlementStatements?.map((statement, i) => {
          return <a className="lms-download-form-field-link" key={`lms-download-form-field-link${i}`} onClick={() => {
            return options.handleDownloadFile(`payoffquotes/download-settlement-statements/${statement?.id}`, { fileName : statement?.assetFile?.name });
          }}>{statement?.assetFile?.name}</a>;
        })}
      </div>;
    })
  ]);

  return form;
};

const LmsServicingPayoffQuotesAddPartialPayoffForm: React.FC<
  LmsServicingPayoffQuotesAddPartialPayoffFormProps
> = ({ loanId, unitId }) => {
  const [{ data: InitialPartialPayoffData, isLoading: isInitialDataLoading, error, fetchData }] = useFetchData((loanId && unitId) ? `payoffquotes/initial-data-for-partial/${loanId}/${unitId}` : "");
  const partialPayoffForm = new BCFormController({ ...InitialPartialPayoffData, exceedsMaturityDateMessage: "", isPartial: true }, {
    submitSuffix: `payoffquotes/generate-payoff-quotation/${unitId}`,
    values: { 
      loanId: loanId,
      loanDetailLoanSubTypeName: InitialPartialPayoffData?.loanDetailLoanSubTypeName,
      loanPropertyUnitReconveyanceId: InitialPartialPayoffData?.loanPropertyUnitReconveyanceId,
      netProceedsOrSweepOrFlat: InitialPartialPayoffData?.netProceedsOrSweepOrFlat,
      showActualSalesPriceMessage: (InitialPartialPayoffData?.actualSalesPrice === null),
      showServicingStatusMessage: (InitialPartialPayoffData?.loanPhaseName === "Servicing" && InitialPartialPayoffData?.loanStatusName !== "Active" ),
      loanDetailRightOfFirstRefusal: InitialPartialPayoffData?.loanDetailRightOfFirstRefusal,
      address: `Property Address ${InitialPartialPayoffData?.propertyStreet} ${InitialPartialPayoffData?.propertyCity} ${InitialPartialPayoffData?.propertyState} ${InitialPartialPayoffData?.propertyZip} Unit # ${InitialPartialPayoffData?.unitReconveyanceDisplay}`
    },
    submitMethod: "POST",
    dataFormatterFunc: (data) => { return {...data, id: guid(), loanId: loanId, additionalInterestBasisId: InitialPartialPayoffData?.additionalInterestBasisId }; },
  });

  addBasicPayoffFormFields(partialPayoffForm);
  addPartialPayoffFields(partialPayoffForm);
  addPayoffQuotesComments(partialPayoffForm);

  useEffect(() => {
    if (InitialPartialPayoffData) { 
      handleInitPartialTotalPayoffCalculation(InitialPartialPayoffData);
      handleExceedsMaturityDateMessage(InitialPartialPayoffData, InitialPartialPayoffData);
    }
  }, [InitialPartialPayoffData]);

  const handleInitPartialTotalPayoffCalculation = async (inputs) => {
    const { data: actualSalePrice } = await fetchData(`payoffquotes/actual-sale-price-data/${loanId}/${unitId}`, { fetchMethod: "GET", skipCache: true });
    if (actualSalePrice?.partialDeedReleaseAmount) {      
      inputs.partialDeedReleaseAmount = actualSalePrice?.partialDeedReleaseAmount;
      getPartialTotalPayoff(inputs, inputs);
    }
  };

  return <LmsFormGenerator
    isLoading={isInitialDataLoading}
    myForm={partialPayoffForm}
    actions={[
      { action: "save", text: "Resync Data" },
      { action: "save", text: "Generate Unit Payoff Quotation" },
      "close",
    ]}
    error={error} 
    onFormChanged={(options, lastUpdated) => onAddPayoffFormChange(options, lastUpdated, InitialPartialPayoffData, fetchData, loanId, true)}
  />;
};

const LmsServicingPayoffQuotesPayoffElegible = () => {
  return (
    <LmsTable
      skipClick
      skipCheckboxAll
      apiSuffix="payoffquotes/payoff-eligible"
      rowsBackgroundCustomColor={(rowData) => rowData.frozen === "Yes" ? "cyan" : ""}
      actionButtons={[
        "export",
        {
          action: "edit",
          text: "Payoff",
          icon: GlobalConstants.Icons.ADD,
          path: `${PAYOFF_QUOTES_BASE_PATH}/${PAYOFF_QUOTES_ROUTES.ADD_PAYOFF}`,
        },
        {
          action: "edit",
          text: "Partial Payoff",
          icon: GlobalConstants.Icons.ADD,
          path: `${PAYOFF_QUOTES_BASE_PATH}/${PAYOFF_QUOTES_ROUTES.PARTIAL_PAYOFF}`,
          isDisabled: (checkedItems: number[], tableData): boolean =>
            checkedItems.length !== 1 ||
            tableData[checkedItems[0]]?.loanNumber[tableData[checkedItems[0]]?.loanNumber?.length - 1] === "M" ||
            tableData[checkedItems[0]].partialPayoffAllowedId === "No" ||
            tableData[checkedItems[0]].frozen === "Yes"
        },
      ]}
      columnLabelsDictionary={{
        loanNumber: "Loan #",
        borrowerOrganization: "Borrower",
        applicationNumber: "Application #",
      }}
    />
  );
};

const LmsServicingPayoffQuotes = ({ params, history }) => {
  const allowedActions = [
    PAYOFF_QUOTES_ROUTES.ADD_PAYOFF,
    PAYOFF_QUOTES_ROUTES.ADD_PARTIAL_PAYOFF,
  ];
  const allowedSubpages = [
    PAYOFF_QUOTES_ROUTES.VIEW_LOAN,
    PAYOFF_QUOTES_ROUTES.PARTIAL_PAYOFF,
    PAYOFF_QUOTES_ROUTES.ELEGIBLE,
  ];
  const { actionLower, isMainPage, isSubpageAllowed, isActionAllowed, id, query } = useParseRouteActions(
    params,
    allowedActions,
    [ ...allowedSubpages, ...allowedActions]
  );

  const [{ data: rawPayoffQuotesData, isLoading: isPayoffQuotesLoading }] = useFetchData("/payoffquotes");
  const payoffQuotesData: IPayoffQuote[] = rawPayoffQuotesData || [];
  const loanId = query?.get("loanId") || "";
  if (!isActionAllowed) {
    history.goBack();
  }

  return (
    <>
      {isMainPage && (
        <LmsServicingPayoffQuotesMainPage />
      )}
      {(isSubpageAllowed && actionLower) && (
        <>
          {actionLower === PAYOFF_QUOTES_ROUTES.VIEW_LOAN && (
            <LmsServicingPayoffQuotesLoanForm 
              loanId={id}
              payoffQuotes={payoffQuotesData}
              isPayoffQuotesLoading={isPayoffQuotesLoading}
            />
          )}
          {actionLower === PAYOFF_QUOTES_ROUTES.ELEGIBLE && (
            <LmsServicingPayoffQuotesPayoffElegible />
          )}
          {actionLower === PAYOFF_QUOTES_ROUTES.ADD_PAYOFF && (
            <LmsServicingPayoffQuotesAddPayoffForm id={id} />
          )}
          {actionLower === PAYOFF_QUOTES_ROUTES.PARTIAL_PAYOFF && (
            <LmsServicingPayoffQuotesPartialPayoff
              loanId={id}
              payoffQuotes={payoffQuotesData}
              isPayoffQuotesLoading={isPayoffQuotesLoading}
            />
          )}
          {actionLower === PAYOFF_QUOTES_ROUTES.ADD_PARTIAL_PAYOFF && (
            <LmsServicingPayoffQuotesAddPartialPayoffForm loanId={loanId} unitId={id} />
          )}
        </>
      )}
    </>
  );
};

export default LmsServicingPayoffQuotes;
